import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { getAppTheme } from '../styles/theme';

const ThemeModeContext = createContext({
  mode: 'light',
  toggleColorMode: () => {},
});

export const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('app-theme-mode');
    return savedMode || 'light';
  });

  useEffect(() => {
    const bg = mode === 'dark' ? '#0f172a' : '#f5f6f8';
    localStorage.setItem('app-theme-mode', mode);
    document.body.dataset.themeMode = mode;
    document.body.style.backgroundColor = bg;
    document.body.style.color = mode === 'dark' ? '#f8fafc' : '#1f2430';
    // The <html> root has no background of its own — if a scrollbar or any
    // sub-pixel layout rounding ever exposes it (e.g. at the page's right
    // edge), it falls back to the browser's default white, which shows as a
    // visible white sliver in dark mode. Keep it in sync with <body> too.
    document.documentElement.style.backgroundColor = bg;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = useMemo(() => getAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      toggleColorMode: () => setMode((prev) => (prev === 'light' ? 'dark' : 'light')),
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
